import { IanaZone, IsoInstant } from '#.shared/dates/types.ts'
import { instantToDateTime } from '#.shared/dates/utils/convert'
import z from 'zod'

import { ShiftOfferStatus } from '../../employee_shift/models/employeeOfferStatus'
import { EmployeeID } from '../../employees/models/employee'

import { ShiftID } from './shift'

export const RoundEmployee = z.object({
  id: EmployeeID,
  name: z.string(),
})

export type RoundEmployeeSchema = z.input<typeof RoundEmployee>
export type RoundEmployee = z.output<typeof RoundEmployee>

export const RoundOffer = z
  .object({
    id: z.string(),
    employee_id: EmployeeID,
    employee: RoundEmployee,
    status: ShiftOfferStatus,
  })
  .transform((obj) => ({
    id: obj.id,
    employeeId: obj.employee_id,
    employee: obj.employee,
    status: obj.status,
  }))

export type RoundOfferSchema = z.input<typeof RoundOffer>
export type RoundOffer = z.output<typeof RoundOffer>

export const RequestRoundID = z.string().brand<'RequestRoundID'>()
export type RequestRoundID = z.infer<typeof RequestRoundID>

export const RoundStatus = z.enum(['pending', 'fired', 'skipped', 'cancelled'])
export type RoundStatus = z.infer<typeof RoundStatus>

export const RequestRound = z
  .object({
    id: RequestRoundID,
    time_zone_id: IanaZone,
    position: z.number(),
    label: z.string().nullable(),
    status: RoundStatus,
    skipped_reason: z.string().nullable(),

    // Definition
    all_eligible: z.boolean(),
    tag_match: z.enum(['any', 'all']),
    tags: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
      }),
    ),
    employees: z.array(RoundEmployee),
    excluded_employees: z.array(RoundEmployee),
    wait_minutes: z.number().nullable(),

    // Timing
    fires_at: IsoInstant.nullable(),
    projected_fires_at: IsoInstant.nullable(),
    fired_at: IsoInstant.nullable(),

    // Pending rounds: who it would resolve to right now
    live_preview: z
      .object({
        count: z.number(),
        sample: z.array(RoundEmployee),
      })
      .nullable(),

    // Fired rounds: who it actually resolved to
    resolved_count: z.number().nullable(),
    offers: z.array(RoundOffer),
  })
  .transform((obj) => ({
    id: obj.id,
    timeZoneId: obj.time_zone_id,
    position: obj.position,
    label: obj.label,
    status: obj.status,
    skippedReason: obj.skipped_reason,

    // Definition
    allEligible: obj.all_eligible,
    tagMatch: obj.tag_match,
    tags: obj.tags.map((t) => ({ id: t.id, name: t.name })),
    employees: obj.employees.map((e) => ({ id: e.id, name: e.name })),
    excludedEmployees: obj.excluded_employees.map((e) => ({
      id: e.id,
      name: e.name,
    })),
    waitMinutes: obj.wait_minutes,

    // Timing
    firesAt: obj.fires_at
      ? instantToDateTime(obj.fires_at, obj.time_zone_id)
      : null,
    projectedFiresAt: obj.projected_fires_at
      ? instantToDateTime(obj.projected_fires_at, obj.time_zone_id)
      : null,
    firedAt: obj.fired_at
      ? instantToDateTime(obj.fired_at, obj.time_zone_id)
      : null,

    // Pending rounds: who it would resolve to right now
    // livePreview: obj.live_preview ? {
    //     count: obj.live_preview.count,
    //     sample: obj.live_preview.sample.map((e) => ({ id: e.id, name: e.name })),
    // } : null,
    livePreview: obj.live_preview
      ? {
          count: obj.live_preview.count,
          sample: obj.live_preview.sample.map((s) => RoundEmployee.parse(s)),
        }
      : null,

    // Fired rounds: who it actually resolved to
    resolvedCount: obj.resolved_count,
    // offers: [] as RoundOffer[],
    offers: obj.offers.map((o) => ({
      id: o.id,
      employeeId: o.employeeId,
      employee: o.employee,
      status: o.status,
    })),
  }))

export type RequestRoundInput = z.input<typeof RequestRound>
export type RequestRound = z.output<typeof RequestRound>

export const RequestQueue = z
  .object({
    shift_id: z.string(),
    time_zone_id: IanaZone,
    default_wait_minutes: z.number(),
    started_at: IsoInstant.nullable(),
    rounds: z.array(RequestRound),
  })
  .transform((obj) => ({
    shiftId: ShiftID.parse(obj.shift_id),
    timeZoneId: obj.time_zone_id,
    defaultWaitMinutes: obj.default_wait_minutes,
    startedAt: obj.started_at
      ? instantToDateTime(obj.started_at, obj.time_zone_id)
      : null,
    rounds: obj.rounds,
  }))

export type RequestQueueSchema = z.input<typeof RequestQueue>
export type RequestQueue = z.output<typeof RequestQueue>
